import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import Loading from '../../components/common/Loading';
import MobileAppsNav from '../../components/common/MobileAppsNav';
import {
  FaFileInvoice,
  FaSignOutAlt,
  FaUndo
} from 'react-icons/fa';
import './BillingDashboard.css';
import logo from '../../assets/img.jpg';

const BillingDashboard = () => {
  const { logout, validateToken } = useAuth();
  const navigate = useNavigate();
  const { data: dashboardData, isLoading, error } = useQuery(
    'billingDashboard',
    () => api.get('/api/billing/dashboard'),
    {
      staleTime: 2 * 60 * 1000, // 2 minutes
      retry: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    }
  );

  const [modalType, setModalType] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState([]);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState(null);
  const [returnCount, setReturnCount] = useState(0);
  const [ledgerStats, setLedgerStats] = useState({ totalEntries: 0, lastBalance: 0 });

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Load count of returns for dashboard card
  React.useEffect(() => {
    const loadCounts = async () => {
      try {
        const res = await api.get('/api/returns');
        const list = res.data?.returns || [];
        setReturnCount(Array.isArray(list) ? list.length : 0);
      } catch (err) {
        setReturnCount(0);
      }
      try {
        const statsRes = await api.get('/api/accounts-ledger/stats');
        const s = statsRes.data?.stats || {};
        setLedgerStats({ totalEntries: s.totalEntries || 0, lastBalance: s.lastBalance || 0 });
      } catch (e) {
        setLedgerStats({ totalEntries: 0, lastBalance: 0 });
      }
    };
    loadCounts();
  }, []);

  // Periodic token validation to prevent automatic logouts
  React.useEffect(() => {
    const interval = setInterval(async () => {
      await validateToken();
    }, 5 * 60 * 1000); // Check every 5 minutes

    return () => clearInterval(interval);
  }, [validateToken]);

  // Fetch data for modal when a card is clicked
  const handleViewClick = async (type) => {
    setModalType(type);
    setShowModal(true);
    setModalLoading(true);
    setModalError(null);
    let url = '';
    let key = '';

    if (type === 'billings') {
      url = '/api/billing';
      key = 'billings';
    } else if (type === 'companies') {
      url = '/api/companies';
      key = 'companies';
    } else if (type === 'quotations') {
      url = '/api/quotations';
      key = 'quotations';
    } else if (type === 'customers') {
      url = '/api/customers';
      key = 'customers';
    } else if (type === 'returns') {
      url = '/api/returns';
      key = 'returns';
    } else if (type === 'accounts-ledger') {
      url = '/api/accounts-ledger';
      key = 'ledgers';
    } else {
      return;
    }

    try {
      const res = await api.get(url);
      setModalData(res.data[key] || res.data);
    } catch (err) {
      setModalError('Failed to fetch data.');
      setModalData([]);
    } finally {
      setModalLoading(false);
    }
  };

  // Axios wraps server response in .data; server payload uses { data: { ... } }
  const payload = dashboardData?.data?.data;
  const stats = payload?.stats || {};
  const recentBillings = payload?.recentBillings || [];
  // const recentQuotations = payload?.recentQuotations || [];

  // Modal content for each card
  const renderModalContent = () => {
    if (modalLoading) return <Loading />;
    if (modalError) return <div style={{ color: 'red' }}>{modalError}</div>;

    if (modalType === 'billings') {
      return (
        <div>
          <h2>All Billings</h2>
          {modalData.length === 0 ? <p>No billings found.</p> : (
            <table className="modal-table">
              <thead>
                <tr>
                  <th>Billing No</th>
                  <th>Company</th>
                  <th>Total Amount</th>
                  <th>Status</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {modalData.map(billing => (
                  <tr key={billing._id}>
                    <td>{billing.billingNo}</td>
                    <td>{billing.company?.name || 'N/A'}</td>
                    <td>₹{billing.totalAmount?.toFixed(2)}</td>
                    <td>{billing.status}</td>
                    <td>{new Date(billing.createdAt).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      );
    }

    if (modalType === 'companies') {
      return (
        <div>
          <h2>All Companies</h2>
          {modalData.length === 0 ? <p>No companies found.</p> : (
            <table className="modal-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Contact Person</th>
                  <th>Email</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {modalData.map(company => (
                  <tr key={company._id}>
                    <td>{company.name}</td>
                    <td>{company.assignedTo?.name || company.contactPerson || 'N/A'}</td>
                    <td>{company.contact?.email || company.contactEmail || 'N/A'}</td>
                    <td>{company.status || 'active'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      );
    }

    if (modalType === 'quotations') {
      return (
        <div>
          <h2>All Quotations</h2>
          {modalData.length === 0 ? <p>No quotations found.</p> : (
            <table className="modal-table">
              <thead>
                <tr>
                  <th>Quotation No</th>
                  <th>Company</th>
                  <th>Total Amount</th>
                  <th>Status</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {modalData.map(quotation => (
                  <tr key={quotation._id}>
                    <td>{quotation.quotationNo}</td>
                    <td>{quotation.company?.name || 'N/A'}</td>
                    <td>₹{quotation.totalAmount?.toFixed(2)}</td>
                    <td>{quotation.status}</td>
                    <td>{new Date(quotation.createdAt).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      );
    }

    if (modalType === 'customers') {
      return (
        <div>
          <h2>All Customers</h2>
          {modalData.length === 0 ? <p>No customers found.</p> : (
            <table className="modal-table">
              <thead>
                <tr>
                  <th>Customer Code</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Type</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {modalData.map(customer => (
                  <tr key={customer._id}>
                    <td>{customer.customerCode}</td>
                    <td>{customer.firstName} {customer.lastName}</td>
                    <td>{customer.email}</td>
                    <td>{customer.customerType}</td>
                    <td>{customer.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      );
    }

    if (modalType === 'returns') {
      return (
        <div>
          <h2>Returned Products</h2>
          {modalData.length === 0 ? <p>No returns found.</p> : (
            <table className="modal-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Item</th>
                  <th>Code</th>
                  <th>Qty</th>
                  <th>Condition</th>
                  <th>Reason</th>
                  <th>Received By</th>
                </tr>
              </thead>
              <tbody>
                {modalData.map(r => (
                  <tr key={r._id}>
                    <td>{new Date(r.createdAt).toLocaleString()}</td>
                    <td>{r.itemName}</td>
                    <td>{r.itemCode}</td>
                    <td>{r.quantity}</td>
                    <td>{r.condition}</td>
                    <td>{r.reason || '-'}</td>
                    <td>{r.receivedBy?.name || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      );
    }
    if (modalType === 'accounts-ledger') {
      return (
        <div>
          <h2>Accounts Ledger</h2>
          {modalData.length === 0 ? <p>No ledger entries found.</p> : (
            <table className="modal-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Account</th>
                  <th>Description</th>
                  <th>Debit</th>
                  <th>Credit</th>
                  <th>Balance</th>
                </tr>
              </thead>
              <tbody>
                {modalData.map(entry => (
                  <tr key={entry._id}>
                    <td>{entry.date ? new Date(entry.date).toLocaleString() : ''}</td>
                    <td>{entry.account?.accountName || '-'}</td>
                    <td>{entry.description || '-'}</td>
                    <td>₹{Number(entry.debit || 0).toFixed(2)}</td>
                    <td>₹{Number(entry.credit || 0).toFixed(2)}</td>
                    <td>₹{Number(entry.runningBalance || 0).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      );
    }

    return null;
  };

  return (
    <div className="dashboard-container">
      {/* Mobile Navigation (Billing Portal) */}
      <MobileAppsNav portal="billing" />

      {/* Desktop Sidebar */}
      <aside className="dashboard-sidebar">
        <img src={logo} alt="AVS Business Logo" className="sidebar-logo" />
        <h2>Billing Menu</h2>
        <nav>
          <ul>
            <li>
              <Link to="/apps.avsbusiness.com/billing"><FaFileInvoice style={{ marginRight: '10px', verticalAlign: 'middle' }} />Billing</Link>
            </li>
            <li>
              <Link to="/apps.avsbusiness.com/accounts-ledger"><FaFileInvoice style={{ marginRight: '10px', verticalAlign: 'middle' }} />Accounts Ledger</Link>
            </li>
            <li>
              <Link to="/apps.avsbusiness.com/client-ledger"><FaFileInvoice style={{ marginRight: '10px', verticalAlign: 'middle' }} />Client Ledger</Link>
            </li>
            <li>
              <Link to="/apps.avsbusiness.com/returns"><FaUndo style={{ marginRight: '10px', verticalAlign: 'middle' }} />Return Product</Link>
            </li>
          </ul>
        </nav>
        <div className="sidebar-logout">
          <button
            onClick={handleLogout}
            className="logout-button"
            style={{
              background: 'linear-gradient(135deg,#ef4444,#f97316)',
              color: '#ffffff',
              border: 'none',
              borderRadius: '8px',
              padding: '12px 20px',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              width: '100%',
              justifyContent: 'center',
              transition: 'transform 0.15s ease, box-shadow 0.15s ease',
              boxShadow: '0 4px 12px rgba(239,68,68,0.3)'
            }}
            onMouseEnter={e => {
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 6px 16px rgba(239,68,68,0.4)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(239,68,68,0.3)';
            }}
          >
            <FaSignOutAlt />
            Logout
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="dashboard-main">
        <h1>AVS BUSINESS Billing Dashboard</h1>
        <p>Manage your billing operations and financial transactions</p>

        {isLoading && <Loading />}
        {error && (
          <div className="error-message" style={{
            padding: '20px',
            backgroundColor: '#fee',
            color: '#c33',
            borderRadius: '8px',
            marginBottom: '20px'
          }}>
            Error loading dashboard data: {error.response?.data?.message || error.message || 'Unknown error'}
          </div>
        )}

        {/* Stats Cards */}
        <div className="dashboard-cards">
          <div className="dashboard-card" role="button" tabIndex={0} onClick={() => handleViewClick('billings')} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleViewClick('billings'); } }}>
            <div className="card-title">Total Billings</div>
            <div className="card-value">{stats.totalBillings || 0}</div>
            <button className="card-link" type="button">View</button>
          </div>
          <div className="dashboard-card" role="button" tabIndex={0} onClick={() => handleViewClick('returns')} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleViewClick('returns'); } }}>
            <div className="card-title">Return Product</div>
            <div className="card-value">{returnCount}</div>
            <button className="card-link" type="button">View</button>
          </div>
          <div className="dashboard-card" role="button" tabIndex={0} onClick={() => handleViewClick('accounts-ledger')} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleViewClick('accounts-ledger'); } }}>
            <div className="card-title">Accounts Ledger</div>
            <div className="card-value">{ledgerStats.totalEntries}</div>
            <button className="card-link" type="button">View</button>
          </div>
        </div>

        <div className="dashboard-section">
          <h2>Recent Billings</h2>
          <div className="section-content">
            {(recentBillings.length === 0) ? (
              <p>No recent billings</p>
            ) : (
              <div>
                {recentBillings.slice(0, 5).map((billing) => (
                  <div key={billing._id}>
                    {billing.billingNo} - {billing.company?.name || 'Unknown Company'} - ₹{billing.totalAmount?.toFixed(2)}
                  </div>
                ))}
              </div>
            )}
            <Link to="/apps.avsbusiness.com/billing" className="section-link">
              View All Billings
            </Link>
          </div>
        </div>


        {/* Modal for details */}
        {showModal && (
          <div className="dashboard-modal-overlay" onClick={() => setShowModal(false)}>
            <div className="dashboard-modal" onClick={e => e.stopPropagation()}>
              <button className="dashboard-modal-close" onClick={() => setShowModal(false)}>&times;</button>
              {renderModalContent()}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default BillingDashboard;
